import { AlertCircle, Wifi, RefreshCw, Search, Frown, Lock, Clock, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface ErrorStateProps {
  type?: 'network' | 'notFound' | 'permission' | 'timeout' | 'generic';
  title?: string;
  message?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

export function ErrorState({
  type = 'generic',
  title,
  message,
  action,
  className = ''
}: ErrorStateProps) {
  const getErrorConfig = () => {
    switch (type) {
      case 'network':
        return {
          icon: Wifi,
          defaultTitle: 'Connection Lost',
          defaultMessage: 'Please check your internet connection and try again.',
          iconColor: 'text-red-500',
          bgClass: 'bg-red-50'
        };
      case 'notFound':
        return {
          icon: Search,
          defaultTitle: 'Not Found',
          defaultMessage: 'We couldn\'t find the page or resource you\'re looking for.',
          iconColor: 'text-gray-500',
          bgClass: 'bg-gray-50'
        };
      case 'permission':
        return {
          icon: Lock,
          defaultTitle: 'Access Denied',
          defaultMessage: 'You do not have permission to view this content.',
          iconColor: 'text-amber-500',
          bgClass: 'bg-amber-50'
        };
      case 'timeout':
        return {
          icon: Clock,
          defaultTitle: 'Request Timeout',
          defaultMessage: 'The request took too long to complete. Please try again.',
          iconColor: 'text-orange-500',
          bgClass: 'bg-orange-50'
        };
      default:
        return {
          icon: AlertCircle,
          defaultTitle: 'Something went wrong',
          defaultMessage: 'An unexpected error occurred. Please try again later.',
          iconColor: 'text-red-500',
          bgClass: 'bg-red-50'
        };
    }
  };

  const config = getErrorConfig();
  const Icon = config.icon;

  return (
    <div className={`text-center py-16 px-6 ${className}`}>
      <div className={`w-16 h-16 ${config.bgClass} rounded-full flex items-center justify-center mx-auto mb-6`}>
        <Icon className={`w-8 h-8 ${config.iconColor}`} />
      </div>

      <h3 className="text-lg font-bold text-gray-900 mb-2">
        {title || config.defaultTitle}
      </h3>

      <p className="text-gray-500 mb-8 max-w-sm mx-auto">
        {message || config.defaultMessage}
      </p>

      {action && (
        <Button
          variant="outline"
          onClick={action.onClick}
          icon={<RefreshCw className="w-4 h-4" />}
        >
          {action.label}
        </Button>
      )}
    </div>
  );
}

export function NetworkError({ onRetry, className }: { onRetry: () => void; className?: string }) {
  return (
    <ErrorState
      type="network"
      action={{ label: 'Try Again', onClick: onRetry }}
      className={className}
    />
  );
}

export function NotFoundError({ resource = 'page', onGoBack, className }: { resource?: string; onGoBack?: () => void; className?: string }) {
  return (
    <ErrorState
      type="notFound"
      title={`${resource.charAt(0).toUpperCase() + resource.slice(1)} Not Found`}
      message={`The requested ${resource} is not available.`}
      action={onGoBack ? { label: 'Go Back', onClick: onGoBack } : undefined}
      className={className}
    />
  );
}

export function PermissionError({ onLogin, className }: { onLogin?: () => void; className?: string }) {
  return (
    <ErrorState
      type="permission"
      action={onLogin ? { label: 'Sign In', onClick: onLogin } : undefined}
      className={className}
    />
  );
}

export function EmptyState({
  icon: Icon = Frown,
  title,
  message,
  action,
  className = ''
}: { icon?: any; title: string; message: string; action?: { label: string; onClick: () => void }; className?: string }) {
  return (
    <div className={`text-center py-16 px-6 rounded-lg border border-gray-100 bg-white ${className}`}>
      <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon className="w-6 h-6 text-gray-400" />
      </div>

      <h3 className="text-base font-semibold text-gray-900 mb-1">
        {title}
      </h3>

      <p className="text-sm text-gray-500 mb-6 max-w-xs mx-auto">
        {message}
      </p>

      {action && (
        <Button
          variant="secondary"
          size="sm"
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      )}
    </div>
  );
}

export function InlineError({ message, onDismiss, className = '' }: { message: string; onDismiss?: () => void; className?: string }) {
  return (
    <div className={`bg-red-50 border border-red-100 rounded-lg p-4 ${className}`}>
      <div className="flex items-start">
        <ShieldAlert className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm text-red-700 font-medium">
            {message}
          </p>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="ml-3 text-red-400 hover:text-red-600 transition-colors"
          >
            <span className="sr-only">Dismiss</span>
            ×
          </button>
        )}
      </div>
    </div>
  );
}

export function FormError({ errors, className = '' }: { errors: Record<string, string>; className?: string }) {
  const errorEntries = Object.entries(errors);
  if (errorEntries.length === 0) return null;

  return (
    <div className={`bg-red-50 border border-red-100 rounded-lg p-4 ${className}`}>
      <div className="flex items-start">
        <AlertCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
        <div className="flex-1 space-y-1">
          <h4 className="text-sm font-medium text-red-800">
            Please correct the following errors:
          </h4>
          <ul className="text-sm text-red-700 list-disc list-inside">
            {errorEntries.map(([field, error]) => (
              <li key={field}>
                {error}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export function ErrorMessage({ message, onRetry, className = '' }: { message: string; onRetry?: () => void; className?: string }) {
  return (
    <div className={`text-center p-6 bg-red-50 rounded-lg border border-red-100 ${className}`}>
      <div className="flex flex-col items-center">
        <AlertCircle className="w-8 h-8 text-red-500 mb-3" />
        <h3 className="text-sm font-bold text-red-900 mb-1">
          Error
        </h3>
        <p className="text-sm text-red-700 mb-4">
          {message}
        </p>

        {onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="border-red-200 text-red-700 hover:bg-red-100 hover:border-red-300 hover:text-red-800"
            icon={<RefreshCw className="w-3 h-3" />}
          >
            Retry
          </Button>
        )}
      </div>
    </div>
  );
}
